// Case management DTOs + the single status/board model, imported by BOTH the server
// (transition validation, board building) and the client (Kanban lanes, labels). Keeping the
// lifecycle here means the API and the UI can never disagree on what a stage means.
//
// This lab is multi-discipline. Clear aligners are the primary discipline and carry a digital-
// ortho lifecycle with a mandatory DOCTOR-APPROVAL gate before manufacturing; restorative work
// (crown/denture/…) skips the scan/planning/approval stages. `caseType` selects the lifecycle
// (CASE_LIFECYCLES) rather than forcing one flow to be correct for every discipline.
import { z } from 'zod';

// --- Discipline / case type --------------------------------------------------

export const CASE_TYPES = ['aligner', 'crown', 'denture', 'nightguard', 'retainer'] as const;
export type CaseType = (typeof CASE_TYPES)[number];

export const CASE_TYPE_LABELS: Record<CaseType, string> = {
  aligner: 'Clear Aligner',
  crown: 'Crown / Bridge',
  denture: 'Denture',
  nightguard: 'Night Guard',
  retainer: 'Retainer',
};

// --- Status model ------------------------------------------------------------

/** Every machine status a case can hold. `status` drives logic; labels below are display. */
export const CASE_STATUSES = [
  'received',
  'scan',
  'planning',
  'approval',
  'production',
  'qc',
  'dispatch',
  'delivered',
  'in_treatment',
  'on_hold',
  'remake',
  'cancelled',
] as const;
export type CaseStatus = (typeof CASE_STATUSES)[number];

/**
 * The Kanban lanes, in order — the aligner superset (widest pipeline). Restorative cases simply
 * never occupy the scan/planning/approval lanes. Decision (12/08/2026): on-board columns below;
 * on_hold/remake/cancelled sit in a collapsible off-board tray rather than as columns.
 */
export const BOARD_COLUMNS: readonly CaseStatus[] = [
  'received',
  'scan',
  'planning',
  'approval',
  'production',
  'qc',
  'dispatch',
  'delivered',
  'in_treatment',
];

/** Statuses shown off-board (side tray), not as their own lanes. */
export const OFF_BOARD_STATUSES: readonly CaseStatus[] = ['on_hold', 'remake', 'cancelled'];

export const CASE_STATUS_LABELS: Record<CaseStatus, string> = {
  received: 'Received',
  scan: 'Scan / Intake',
  planning: 'Treatment Planning',
  approval: 'Doctor Approval',
  production: 'Production',
  qc: 'QC',
  dispatch: 'Ready for Dispatch',
  delivered: 'Delivered',
  in_treatment: 'In Treatment',
  on_hold: 'On Hold',
  remake: 'Remake',
  cancelled: 'Cancelled',
};

/**
 * Ordered on-board pipeline per discipline. Aligner runs the full digital-ortho flow with the
 * approval gate; restorative disciplines skip scan/planning/approval. The board renders the
 * aligner superset (BOARD_COLUMNS); a restorative case just never lands in the skipped lanes.
 */
export const CASE_LIFECYCLES: Record<CaseType, readonly CaseStatus[]> = {
  aligner: ['received', 'scan', 'planning', 'approval', 'production', 'qc', 'dispatch', 'delivered', 'in_treatment'],
  crown: ['received', 'production', 'qc', 'dispatch', 'delivered'],
  denture: ['received', 'production', 'qc', 'dispatch', 'delivered'],
  nightguard: ['received', 'production', 'qc', 'dispatch', 'delivered'],
  retainer: ['received', 'production', 'qc', 'dispatch', 'delivered'],
};

/** The on-board pipeline for a discipline. */
export function pipelineFor(caseType: CaseType): readonly CaseStatus[] {
  return CASE_LIFECYCLES[caseType];
}

/** True once a case has reached (or passed) the approval gate in the aligner flow. */
export function requiresApprovalGate(caseType: CaseType): boolean {
  return CASE_LIFECYCLES[caseType].includes('approval');
}

/**
 * The linear "advance" target — the next on-board stage for this discipline — used by the
 * QR/quick-advance action. Off-board and terminal statuses have no linear next (returns null).
 */
export function nextStage(caseType: CaseType, from: CaseStatus): CaseStatus | null {
  const pipeline = CASE_LIFECYCLES[caseType];
  const idx = pipeline.indexOf(from);
  if (idx === -1 || idx === pipeline.length - 1) return null;
  return pipeline[idx + 1] ?? null;
}

/**
 * Legal status transitions given the case's discipline:
 *  - forward one step along the discipline's pipeline
 *  - approval → planning (doctor requested a setup revision; aligner only)
 *  - qc → remake (QC reject)
 *  - remake → production (rework re-enters manufacturing)
 *  - any active on-board stage (not delivered) → on_hold | cancelled
 *  - on_hold → resume to any active stage in the discipline's pipeline
 * NOTE: for the aligner flow this returns approval → production, but that specific move is the
 * doctor sign-off and is performed ONLY through the case:approve_setup path — the generic status
 * endpoint rejects it (see server service.changeStatus).
 */
export function allowedTransitions(caseType: CaseType, from: CaseStatus): CaseStatus[] {
  const pipeline = CASE_LIFECYCLES[caseType];
  const idx = pipeline.indexOf(from);
  const out = new Set<CaseStatus>();
  if (idx >= 0 && idx < pipeline.length - 1) out.add(pipeline[idx + 1]!);
  if (from === 'approval') out.add('planning');
  if (from === 'qc') out.add('remake');
  if (from === 'remake') out.add('production');
  if (idx >= 0 && from !== 'delivered') {
    out.add('on_hold');
    out.add('cancelled');
  }
  if (from === 'on_hold') for (const s of pipeline) if (s !== 'delivered') out.add(s);
  return [...out];
}

/** True if `to` is a legal next status from `from` for this discipline. */
export function canTransition(caseType: CaseType, from: CaseStatus, to: CaseStatus): boolean {
  return allowedTransitions(caseType, from).includes(to);
}

// --- Arch, priority, relationship -------------------------------------------

export const CASE_ARCHES = ['upper', 'lower', 'both'] as const;
export type CaseArch = (typeof CASE_ARCHES)[number];
export const CASE_ARCH_LABELS: Record<CaseArch, string> = {
  upper: 'Upper',
  lower: 'Lower',
  both: 'Both arches',
};

export const CASE_PRIORITIES = ['normal', 'urgent', 'same_day'] as const;
export type CasePriority = (typeof CASE_PRIORITIES)[number];
export const CASE_PRIORITY_LABELS: Record<CasePriority, string> = {
  normal: 'Normal',
  urgent: 'Urgent',
  same_day: 'Same-day',
};

/**
 * How a child case relates to its parent. A `remake` is the lab's fault (redo, usually lab cost);
 * a `refinement` is a normal planned new phase (new scan → setup → approval → production, often
 * contractually included/discounted); a `retainer` is a separate end-of-treatment deliverable;
 * `continuation` is a follow-on course. This deliberately replaces the old boolean isRemake,
 * which conflated remakes with refinements.
 */
export const CASE_RELATION_TYPES = ['remake', 'refinement', 'retainer', 'continuation'] as const;
export type CaseRelationType = (typeof CASE_RELATION_TYPES)[number];
export const CASE_RELATION_LABELS: Record<CaseRelationType, string> = {
  remake: 'Remake',
  refinement: 'Refinement',
  retainer: 'Retainer',
  continuation: 'Continuation',
};

// --- Shapes ------------------------------------------------------------------

export interface Case {
  id: string;
  caseNumber: string;
  caseType: CaseType;
  clientId: string | null;
  clientName: string;
  patientCode: string | null;
  service: string;
  arch: CaseArch | null;
  tooth: string | null;
  /** Aligner step counts per arch (drive complexity/pricing). Null for restorative. */
  totalStagesUpper: number | null;
  totalStagesLower: number | null;
  /** Aligner sheet material / brand (e.g. Zendura FLX, Duran). Null for restorative. */
  alignerMaterial: string | null;
  priority: CasePriority;
  status: CaseStatus;
  assignedTo: string | null;
  dueDate: string | null;
  receivedAt: string;
  notes: string | null;
  /** Set on a child case (refinement/remake/retainer/continuation of parentCaseId). */
  relationType: CaseRelationType | null;
  parentCaseId: string | null;
  /** 0 = initial course; 1,2,… = refinement round. */
  refinementRound: number;
  /** Denormalized doctor-approval stamp (the source of truth is a case_events row). */
  setupApprovedAt: string | null;
  setupApprovedByUserId: string | null;
  /** How many times the doctor bounced the setup back for revision. */
  setupRevisionCount: number;
  /** Aligner punching (trim-line) machine + free-text instructions. Null when unset. */
  punchingType: string | null;
  punchingInstructions: string | null;
  /** QC checklist items (label + done). Empty array when unset. */
  checklist: ChecklistItem[];
  /** patient_media id of a setup simulation shared to the patient portal, or null. */
  simulationMediaId: string | null;
  /** Soft-delete stamp; null = live. Deleted cases are filtered from lists/board/detail. */
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** A single QC checklist row. */
export interface ChecklistItem {
  label: string;
  done: boolean;
}

export interface CaseEvent {
  id: string;
  caseId: string;
  at: string;
  fromStatus: CaseStatus | null;
  toStatus: CaseStatus;
  actorUserId: string | null;
  note: string | null;
}

/** A single Kanban column with its cases (newest-first). */
export interface BoardColumn {
  status: CaseStatus;
  label: string;
  cases: Case[];
}

/** The full board payload: ordered on-board columns + the off-board tray. */
export interface CaseBoard {
  columns: BoardColumn[];
  offBoard: BoardColumn[];
}

// --- Validation schemas ------------------------------------------------------

const trimmed = (max: number) => z.string().trim().min(1).max(max);

export const createCaseSchema = z.object({
  caseType: z.enum(CASE_TYPES).default('aligner'),
  clientName: trimmed(160),
  clientId: z.string().min(1).nullish(),
  service: trimmed(120),
  patientCode: z.string().trim().max(60).nullish(),
  arch: z.enum(CASE_ARCHES).nullish(),
  tooth: z.string().trim().max(60).nullish(),
  totalStagesUpper: z.coerce.number().int().min(0).max(200).nullish(),
  totalStagesLower: z.coerce.number().int().min(0).max(200).nullish(),
  alignerMaterial: z.string().trim().max(80).nullish(),
  priority: z.enum(CASE_PRIORITIES).default('normal'),
  dueDate: z.string().min(1).nullish(), // ISO date; UI sends YYYY-MM-DD
  assignedTo: z.string().min(1).nullish(),
  notes: z.string().trim().max(2000).nullish(),
  // Child-case linkage (refinements/remakes/retainers spawned off a parent).
  relationType: z.enum(CASE_RELATION_TYPES).nullish(),
  parentCaseId: z.string().min(1).nullish(),
});
export type CreateCaseRequest = z.infer<typeof createCaseSchema>;

export const updateCaseSchema = z
  .object({
    clientName: trimmed(160).optional(),
    service: trimmed(120).optional(),
    patientCode: z.string().trim().max(60).nullish(),
    arch: z.enum(CASE_ARCHES).nullish(),
    tooth: z.string().trim().max(60).nullish(),
    totalStagesUpper: z.coerce.number().int().min(0).max(200).nullish(),
    totalStagesLower: z.coerce.number().int().min(0).max(200).nullish(),
    alignerMaterial: z.string().trim().max(80).nullish(),
    priority: z.enum(CASE_PRIORITIES).optional(),
    dueDate: z.string().min(1).nullish(),
    assignedTo: z.string().min(1).nullish(),
    notes: z.string().trim().max(2000).nullish(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'No fields to update' });
export type UpdateCaseRequest = z.infer<typeof updateCaseSchema>;

/** Explicit status change (QC approve/reject, cancel, hold, resume, etc.). */
export const setStatusSchema = z.object({
  status: z.enum(CASE_STATUSES),
  note: z.string().trim().max(500).nullish(),
});
export type SetStatusRequest = z.infer<typeof setStatusSchema>;

/** Quick/QR advance to the next on-board stage — no target needed. */
export const advanceCaseSchema = z.object({
  note: z.string().trim().max(500).nullish(),
});
export type AdvanceCaseRequest = z.infer<typeof advanceCaseSchema>;

/** Treating-doctor sign-off of the digital setup (approval → production). Gated case:approve_setup. */
export const approveSetupSchema = z.object({
  note: z.string().trim().max(500).nullish(),
});
export type ApproveSetupRequest = z.infer<typeof approveSetupSchema>;

/** Doctor bounces the setup back for revision (approval → planning) with a reason. */
export const requestRevisionSchema = z.object({
  note: z.string().trim().min(1).max(500),
});
export type RequestRevisionRequest = z.infer<typeof requestRevisionSchema>;

/** Replace the case QC checklist wholesale. */
export const setChecklistSchema = z.object({
  items: z
    .array(z.object({ label: z.string().trim().min(1).max(200), done: z.boolean() }))
    .max(100),
});
export type SetChecklistRequest = z.infer<typeof setChecklistSchema>;

/** Aligner punching (trim-line) machine + instructions. */
export const setPunchingSchema = z.object({
  punchingType: z.string().trim().max(80).nullish(),
  punchingInstructions: z.string().trim().max(2000).nullish(),
});
export type SetPunchingRequest = z.infer<typeof setPunchingSchema>;

/** Share (or clear) a setup-simulation media item to the patient portal. */
export const setSimulationSchema = z.object({
  mediaId: z.string().min(1).nullable(),
});
export type SetSimulationRequest = z.infer<typeof setSimulationSchema>;

export interface CaseListQuery {
  status?: CaseStatus;
  caseType?: CaseType;
  clientId?: string;
  assignedTo?: string;
  search?: string;
  limit: number;
  offset: number;
}
